import React, { useState, useMemo } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';

function CopyWeekModal({
  show,
  onHide,
  sourceWeek,
  sourcePhase,
  allWorkouts,
  onConfirm
}) {
  const [targetWeek, setTargetWeek] = useState('');

  // Count workouts in source week
  const sourceWorkoutCount = useMemo(() => {
    return allWorkouts.filter(w =>
      w.week === sourceWeek &&
      (sourcePhase === null || w.phase === sourcePhase)
    ).length;
  }, [allWorkouts, sourceWeek, sourcePhase]);

  // Get existing weeks for reference
  const existingWeeks = useMemo(() => {
    return [...new Set(
      allWorkouts
        .filter(w => sourcePhase === null || w.phase === sourcePhase)
        .map(w => w.week)
        .filter(Boolean)
    )].sort((a, b) => a - b);
  }, [allWorkouts, sourcePhase]);

  // Count workouts in target week
  const targetWorkoutCount = useMemo(() => {
    if (!targetWeek) return 0;
    const targetWeekNum = parseInt(targetWeek, 10);
    return allWorkouts.filter(w =>
      w.week === targetWeekNum &&
      (sourcePhase === null || w.phase === sourcePhase)
    ).length;
  }, [allWorkouts, targetWeek, sourcePhase]);

  const handleConfirm = () => {
    if (!targetWeek) {
      alert('Please enter a target week number');
      return;
    }
    const targetWeekNum = parseInt(targetWeek, 10);
    if (isNaN(targetWeekNum) || targetWeekNum < 1 || targetWeekNum > 52) {
      alert('Please enter a valid week number between 1 and 52');
      return;
    }
    if (targetWeekNum === sourceWeek) {
      alert('Cannot copy week to itself');
      return;
    }
    onConfirm(targetWeekNum);
    onHide();
  };

  const handleClose = () => {
    setTargetWeek('');
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fas fa-copy me-2"></i>
          Copy Week {sourceWeek}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <p className="text-muted">
            Copy all workouts from <strong>Week {sourceWeek}</strong> to another week.
            {sourcePhase !== null && ` (Phase ${sourcePhase})`}
          </p>
        </div>

        <Form.Group className="mb-3">
          <Form.Label>Target Week Number</Form.Label>
          <Form.Control
            type="number"
            min="1"
            max="52"
            value={targetWeek}
            onChange={(e) => setTargetWeek(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleConfirm();
              }
            }}
            placeholder="Enter week number (1-52)"
            autoFocus
          />
          {existingWeeks.includes(parseInt(targetWeek, 10)) && targetWeek && (
            <Form.Text className="text-warning">
              <i className="fas fa-exclamation-triangle me-1"></i>
              Week {targetWeek} already has workouts
            </Form.Text>
          )}
        </Form.Group>

        {targetWeek && (
          <Alert variant="info" className="mb-0">
            <div className="d-flex align-items-start">
              <i className="fas fa-info-circle me-2 mt-1"></i>
              <div>
                <strong>Preview:</strong>
                <p className="mb-1">
                  Copy {sourceWorkoutCount} workout{sourceWorkoutCount !== 1 ? 's' : ''} from Week {sourceWeek} to Week {targetWeek}
                </p>
                {targetWorkoutCount > 0 && (
                  <p className="mb-0 text-warning">
                    <i className="fas fa-exclamation-triangle me-1"></i>
                    Week {targetWeek} already has {targetWorkoutCount} workout{targetWorkoutCount !== 1 ? 's' : ''}.
                    The copied workouts will be added to the end.
                  </p>
                )}
              </div>
            </div>
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={
            !targetWeek ||
            isNaN(parseInt(targetWeek, 10)) ||
            parseInt(targetWeek, 10) < 1 ||
            parseInt(targetWeek, 10) > 52 ||
            parseInt(targetWeek, 10) === sourceWeek
          }
        >
          <i className="fas fa-copy me-2"></i>
          Copy Week
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default CopyWeekModal;
